import React, { useState } from 'react';
import { X, Calendar, User, Mail, Phone, Clock, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CrearCitaManualModal = ({ fecha, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    nombreCompleto: '',
    cedula: '',
    telefono: '',
    email: '',
    fecha: fecha, // Pre-llenada con el día seleccionado
    hora: ''
  });
  const [creando, setCreando] = useState(false);

  // Formato de cédula: 11.111.111
  const formatCedula = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}`;
  };

  // Formato de teléfono: 0111-1111111
  const formatTelefono = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 4) return numbers;
    return `${numbers.slice(0, 4)}-${numbers.slice(4, 11)}`;
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    if (field === 'cedula') {
      formattedValue = formatCedula(value);
    } else if (field === 'telefono') {
      formattedValue = formatTelefono(value);
    }

    setFormData({
      ...formData,
      [field]: formattedValue
    });
  };

  const handleCrear = async () => {
    // Validaciones
    if (!formData.nombreCompleto.trim()) {
      toast.error('El nombre completo es obligatorio');
      return;
    }

    if (!formData.cedula.trim()) {
      toast.error('La cédula es obligatoria');
      return;
    }

    if (!formData.telefono.trim()) {
      toast.error('El teléfono es obligatorio');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('El correo electrónico es obligatorio');
      return;
    }

    if (!formData.fecha) {
      toast.error('La fecha es obligatoria');
      return;
    }

    if (!formData.hora) {
      toast.error('La hora es obligatoria');
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('El correo electrónico no es válido');
      return;
    }

    setCreando(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/gestor-citas/cita-manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          nombre_completo: formData.nombreCompleto,
          cedula: formData.cedula,
          telefono: formData.telefono,
          email: formData.email,
          fecha: formData.fecha,
          hora: formData.hora
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Cita manual creada exitosamente. Email de confirmación enviado.');
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear cita manual');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión al crear cita');
    } finally {
      setCreando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
      <div className="bg-slate-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-gray-700 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-yellow-500" />
            <div>
              <h2 className="text-xl font-bold text-white">Crear Cita Manual</h2>
              <p className="text-gray-400 text-sm">Registro de nueva cita por Departamento de Retiros</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={creando}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Nombre Completo */}
          <div>
            <label className="flex items-center gap-2 text-white font-semibold mb-2">
              <User className="w-4 h-4" />
              Nombre Completo <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.nombreCompleto}
              onChange={(e) => handleInputChange('nombreCompleto', e.target.value)}
              placeholder="Juan Pérez García"
              className="bg-slate-800 border-gray-700 text-white"
              disabled={creando}
            />
          </div>

          {/* Cédula */}
          <div>
            <label className="flex items-center gap-2 text-white font-semibold mb-2">
              <CreditCard className="w-4 h-4" />
              Cédula <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.cedula}
              onChange={(e) => handleInputChange('cedula', e.target.value)}
              placeholder="12.345.678"
              className="bg-slate-800 border-gray-700 text-white"
              maxLength={10}
              disabled={creando}
            />
          </div>

          {/* Teléfono */}
          <div>
            <label className="flex items-center gap-2 text-white font-semibold mb-2">
              <Phone className="w-4 h-4" />
              Teléfono <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={formData.telefono}
              onChange={(e) => handleInputChange('telefono', e.target.value)}
              placeholder="0414-1234567"
              className="bg-slate-800 border-gray-700 text-white"
              maxLength={12}
              disabled={creando}
            />
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 text-white font-semibold mb-2">
              <Mail className="w-4 h-4" />
              Correo Electrónico <span className="text-red-500">*</span>
            </label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="cliente@ejemplo.com"
              className="bg-slate-800 border-gray-700 text-white"
              disabled={creando}
            />
          </div>

          {/* Fecha */}
          <div>
            <label className="flex items-center gap-2 text-white font-semibold mb-2">
              <Calendar className="w-4 h-4" />
              Fecha <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              value={formData.fecha}
              onChange={(e) => handleInputChange('fecha', e.target.value)}
              className="bg-slate-800 border-gray-700 text-white"
              disabled={creando}
            />
          </div>

          {/* Hora */}
          <div>
            <label className="flex items-center gap-2 text-white font-semibold mb-2">
              <Clock className="w-4 h-4" />
              Hora <span className="text-red-500">*</span>
            </label>
            <Input
              type="time"
              value={formData.hora}
              onChange={(e) => handleInputChange('hora', e.target.value)}
              className="bg-slate-800 border-gray-700 text-white"
              disabled={creando}
            />
            <p className="text-xs text-gray-400 mt-1">
              Selecciona cualquier hora disponible. No hay restricciones de cupos para citas manuales.
            </p>
          </div>

          {/* Nota informativa */}
          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
            <p className="text-blue-400 text-sm">
              <strong>Nota:</strong> Esta cita será creada manualmente por el Departamento de Retiros. 
              Se generará un link de Google Meet y se enviará un email automático de confirmación al cliente.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900 border-t border-gray-700 p-6 flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="border-gray-700 text-gray-400 hover:bg-gray-800"
            disabled={creando}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCrear}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
            disabled={creando}
          >
            {creando ? 'Creando...' : 'Crear Cita'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CrearCitaManualModal;
